import { vercel } from '@t3-oss/env-core/presets-zod';
import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

export const env = createEnv({
  server: {
    // Sentry source-map upload (build-time only) — optional so a missing/empty
    // token (e.g. SENTRY_AUTH_TOKEN "Needs Attention") never fails the build.
    SENTRY_ORG: z.string().optional(),
    SENTRY_PROJECT: z.string().optional(),
    SENTRY_AUTH_TOKEN: z.string().optional(),
    INDEXING_DISABLED: z.string().optional(),
  },
  client: {
    // Required — the app can't function without these.
    NEXT_PUBLIC_STRAPI_URL: z.url(),
    NEXT_PUBLIC_API_URL: z.url(),
    // Optional integrations — absent = feature simply disabled.
    NEXT_PUBLIC_MICROSOFT_CLARITY_PROJECT_ID: z.string().optional(),
    NEXT_PUBLIC_SENTRY_DSN: z.url().optional(),
  },
  // If you're using Next.js < 13.4.4, you'll need to specify the runtimeEnv manually
  runtimeEnv: {
    NEXT_PUBLIC_STRAPI_URL: process.env.NEXT_PUBLIC_STRAPI_URL,
    NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
    NEXT_PUBLIC_MICROSOFT_CLARITY_PROJECT_ID:
      process.env.NEXT_PUBLIC_MICROSOFT_CLARITY_PROJECT_ID,
    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
    SENTRY_ORG: process.env.SENTRY_ORG,
    SENTRY_PROJECT: process.env.SENTRY_PROJECT,
    SENTRY_AUTH_TOKEN: process.env.SENTRY_AUTH_TOKEN,
    INDEXING_DISABLED: process.env.INDEXING_DISABLED,
  },
  // For Next.js >= 13.4.4, you only need to destructure client variables:
  // experimental__runtimeEnv: {
  //   NEXT_PUBLIC_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_PUBLISHABLE_KEY,
  // }
  extends: [vercel()],
  // Treat empty strings (e.g. unset Vercel vars) as missing.
  emptyStringAsUndefined: true,
  // Escape hatch: set SKIP_ENV_VALIDATION=1 to bypass validation (e.g. for a
  // first deploy before every integration var is configured). The app still
  // needs NEXT_PUBLIC_API_URL at runtime to talk to the backend.
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
  // Surface the exact offending variable(s) in the thrown message so build logs
  // name the culprit instead of a generic "Invalid environment variables".
  onValidationError: (issues: unknown): never => {
    const list = Array.isArray(issues)
      ? issues
      : ((issues as { issues?: unknown[] })?.issues ?? []);
    const detail = (list as Array<{ path?: unknown[]; message?: string }>)
      .map((i) => `${(i.path ?? []).join('.') || '(root)'}: ${i.message ?? 'invalid'}`)
      .join('; ');
    throw new Error(
      `Invalid environment variables → ${detail || JSON.stringify(issues)}`
    );
  },
});
